import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { 
  Settings, 
  Layers, 
  AlertTriangle, 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  CheckCircle,
  Search,
  ChevronRight,
  ChevronDown,
  Info,
  Truck,
  Wrench,
  Package,
  User,
  RefreshCw,
  ArrowRight,
  Clipboard,
  XCircle,
  DollarSign,
  Award,
  Cpu,
  Battery,
  Camera,
  Monitor,
  Wifi,
  Volume2,
  Box,
  Plug,
  Code,
  Shield,
  FileText,
  Headphones,
  BatteryCharging
} from 'lucide-react';
import axios from '../lib/axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Zobox theme colors
const THEME = {
  primary: '#2b3380',
  primaryLight: '#e8eaf6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#8B5CF6'
};

// Icon mapping
const IconMap = {
  'user': User, 'truck': Truck, 'wrench': Wrench, 'check-circle': CheckCircle,
  'package': Package, 'info': Info, 'alert-triangle': AlertTriangle,
  'x-circle': XCircle, 'trash-2': Trash2, 'circle': CheckCircle,
  'clipboard': Clipboard, 'monitor': Monitor, 'battery': Battery,
  'camera': Camera, 'box': Box, 'volume-2': Volume2, 'wifi': Wifi,
  'plug': Plug, 'code': Code, 'shield': Shield, 'file-text': FileText,
  'headphones': Headphones, 'battery-charging': BatteryCharging
};

const DynamicIcon = ({ name, className, style }) => {
  const Icon = IconMap[name] || Settings;
  return <Icon className={className} style={style} />;
};

export default function QCControlModule({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('types');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [qcTypes, setQcTypes] = useState([]);
  const [severityMatrix, setSeverityMatrix] = useState([]);
  const [categories, setCategories] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [grades, setGrades] = useState([]);
  const [weights, setWeights] = useState([]);
  const [multipliers, setMultipliers] = useState([]);
  const [topups, setTopups] = useState([]);
  const [partsMapping, setPartsMapping] = useState([]);
  const [partsList, setPartsList] = useState([]);
  const [formula, setFormula] = useState(null);
  const [stats, setStats] = useState({});
  
  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedParts, setExpandedParts] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({});

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        typesRes, severityRes, categoriesRes, paramsRes, statsRes,
        gradesRes, weightsRes, multipliersRes, topupsRes, partsMappingRes, partsListRes, formulaRes
      ] = await Promise.all([
        axios.get(`${API_URL}/api/qc-control/types`),
        axios.get(`${API_URL}/api/qc-control/severity-matrix`),
        axios.get(`${API_URL}/api/qc-control/categories`),
        axios.get(`${API_URL}/api/qc-control/parameters`),
        axios.get(`${API_URL}/api/qc-control/dashboard/stats`),
        axios.get(`${API_URL}/api/qc-control/grades`),
        axios.get(`${API_URL}/api/qc-control/pricing/weights`),
        axios.get(`${API_URL}/api/qc-control/pricing/multipliers`),
        axios.get(`${API_URL}/api/qc-control/pricing/topups`),
        axios.get(`${API_URL}/api/qc-control/parts/mapping`),
        axios.get(`${API_URL}/api/qc-control/parts/list`),
        axios.get(`${API_URL}/api/qc-control/pricing/formula`)
      ]);
      
      setQcTypes(typesRes.data.data || []);
      setSeverityMatrix(severityRes.data.data || []);
      setCategories(categoriesRes.data.data || []);
      setParameters(paramsRes.data.data || []);
      setStats(statsRes.data.data || {});
      setGrades(gradesRes.data.data || []);
      setWeights(weightsRes.data.data || []);
      setMultipliers(multipliersRes.data.data || []);
      setTopups(topupsRes.data.data || []);
      setPartsMapping(partsMappingRes.data.data || []);
      setPartsList(partsListRes.data.data || []);
      setFormula(formulaRes.data.data || null);
    } catch (error) {
      console.error('Error fetching QC data:', error);
      toast.error('Failed to load QC configuration data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleCategory = (id) => setExpandedCategories(prev => ({ ...prev, [id]: !prev[id] }));
  const togglePart = (id) => setExpandedParts(prev => ({ ...prev, [id]: !prev[id] }));

  const handleAdd = (type) => {
    setModalType(type);
    setEditingItem(null);
    setFormData(getDefaultFormData(type));
    setShowAddModal(true);
  };

  const handleEdit = (type, item) => {
    setModalType(type);
    setEditingItem(item);
    setFormData(item);
    setShowAddModal(true);
  };

  const handleDelete = async (type, itemId) => {
    if (!window.confirm('Are you sure you want to deactivate this item?')) return;
    try {
      const endpoints = {
        'type': `${API_URL}/api/qc-control/types/${itemId}`,
        'severity': `${API_URL}/api/qc-control/severity-matrix/${itemId}`,
        'category': `${API_URL}/api/qc-control/categories/${itemId}`,
        'parameter': `${API_URL}/api/qc-control/parameters/${itemId}`,
        'topup': `${API_URL}/api/qc-control/pricing/topups/${itemId}`
      };
      await axios.delete(endpoints[type]);
      toast.success('Item deactivated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  const getDefaultFormData = (type) => {
    const defaults = {
      'type': { stage_id: '', name: '', full_name: '', purpose: '', performed_by: '', output: '', grade_generated: false, icon: 'clipboard', color: THEME.primary },
      'severity': { severity_id: '', name: '', description: '', points: 0, color: '#6B7280', bg_color: '#F3F4F6', grade_impact: 'None' },
      'category': { category_id: '', name: '', icon: 'folder', description: '', is_gating: false, order: 99 },
      'parameter': { parameter_id: '', name: '', category_id: '', description: '', is_gating: false, options: [] },
      'topup': { topup_id: '', name: '', amount: 0, icon: 'package' },
      'part': { part_id: '', name: '', category: 'General', avg_cost: 0 }
    };
    return defaults[type] || {};
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const endpoints = {
        'type': editingItem ? `${API_URL}/api/qc-control/types/${editingItem.stage_id}` : `${API_URL}/api/qc-control/types`,
        'severity': editingItem ? `${API_URL}/api/qc-control/severity-matrix/${editingItem.severity_id}` : `${API_URL}/api/qc-control/severity-matrix`,
        'category': editingItem ? `${API_URL}/api/qc-control/categories/${editingItem.category_id}` : `${API_URL}/api/qc-control/categories`,
        'parameter': editingItem ? `${API_URL}/api/qc-control/parameters/${editingItem.parameter_id}` : `${API_URL}/api/qc-control/parameters`,
        'topup': editingItem ? `${API_URL}/api/qc-control/pricing/topups/${editingItem.topup_id}` : `${API_URL}/api/qc-control/pricing/topups`,
        'part': editingItem ? `${API_URL}/api/qc-control/parts/${editingItem.part_id}` : `${API_URL}/api/qc-control/parts`
      };
      const method = editingItem ? 'put' : 'post';
      await axios[method](endpoints[modalType], formData);
      toast.success(editingItem ? 'Updated successfully!' : 'Created successfully!');
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInlineUpdate = async (type, id, field, value) => {
    try {
      const endpoints = {
        'grade': `${API_URL}/api/qc-control/grades/${id}`,
        'weight': `${API_URL}/api/qc-control/pricing/weights/${id}`,
        'multiplier': `${API_URL}/api/qc-control/pricing/multipliers/${id}`,
        'topup': `${API_URL}/api/qc-control/pricing/topups/${id}`,
        'part': `${API_URL}/api/qc-control/parts/${id}`
      };
      await axios.put(endpoints[type], { [field]: value });
      toast.success('Updated!');
      fetchData();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const getCategoryEmoji = (name) => {
    const emojiMap = {
      'Device': '📱', 'Display': '🖥️', 'Battery': '🔋', 'Network': '📶',
      'Camera': '📷', 'Audio': '🔊', 'Button': '🔘', 'Physical': '📦',
      'Port': '🔌', 'Software': '💾', 'Packaging': '📦', 'Warranty': '🛡️'
    };
    for (const [key, emoji] of Object.entries(emojiMap)) {
      if (name?.includes(key)) return emoji;
    }
    return '📋';
  };

  const filteredParameters = parameters.map(cat => ({
    ...cat,
    parameters: cat.parameters.filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.parameter_id.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(cat => cat.parameters.length > 0);

  // ==================== RENDER TABS ====================

  // Tab 1: QC Types
  const renderQCTypes = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: THEME.primary }}>QC Lifecycle Flow</h3>
        <div className="flex items-center justify-between overflow-x-auto pb-2">
          {qcTypes.filter(t => t.is_active).map((type, index) => (
            <React.Fragment key={type.stage_id}>
              <div className="flex flex-col items-center min-w-[120px] p-3 rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => handleEdit('type', type)}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2 border-2" style={{ backgroundColor: type.color + '15', borderColor: type.color }}>
                  <DynamicIcon name={type.icon} className="w-7 h-7" style={{ color: type.color }} />
                </div>
                <span className="text-sm font-bold" style={{ color: THEME.primary }}>{type.stage_id.toUpperCase()}</span>
                <span className="text-xs text-gray-500 text-center">{type.name.split('-')[1]?.trim() || type.name}</span>
              </div>
              {index < qcTypes.filter(t => t.is_active).length - 1 && <ArrowRight className="w-6 h-6 text-gray-400 mx-2" />}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {qcTypes.map(type => (
          <div key={type.stage_id} className="bg-white rounded-lg shadow-md p-5 border-l-4 hover:shadow-lg" style={{ borderLeftColor: type.color }} data-testid={`qc-type-card-${type.stage_id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: type.color + '15' }}>
                  <DynamicIcon name={type.icon} className="w-5 h-5" style={{ color: type.color }} />
                </div>
                <div>
                  <h4 className="font-bold" style={{ color: THEME.primary }}>{type.stage_id.toUpperCase()}</h4>
                  <p className="text-xs text-gray-500">{type.full_name}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => handleEdit('type', type)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete('type', type.stage_id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-3">{type.purpose}</p>
            <div className="space-y-2 text-xs border-t pt-3">
              <div className="flex justify-between"><span className="text-gray-500">Performed By:</span><span className="text-gray-700 font-medium">{type.performed_by?.split('(')[0]?.trim()}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Grade Generated:</span><span className={type.grade_generated ? 'text-green-600 font-medium' : 'text-gray-400'}>{type.grade_generated ? 'Yes' : 'No'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Parameters:</span><span className="font-medium" style={{ color: THEME.primary }}>{type.parameters_enabled || 0}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Tab 2: Parameters
  const renderParameters = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2"><Settings className="w-4 h-4" style={{ color: THEME.primary }} /><span className="text-gray-500">Total:</span><span className="font-bold" style={{ color: THEME.primary }}>{stats.parameters || 0}</span></div>
          <div className="flex items-center gap-2"><Layers className="w-4 h-4 text-blue-600" /><span className="text-gray-500">Categories:</span><span className="font-bold text-blue-600">{stats.categories || 0}</span></div>
          <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-500" /><span className="text-gray-500">Gating:</span><span className="font-bold text-orange-500">{stats.gating_parameters || 0}</span></div>
        </div>
      </div>
      <div className="space-y-3">
        {filteredParameters.map(category => (
          <div key={category.category_id} className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 border-l-4" style={{ borderLeftColor: THEME.primary }} onClick={() => toggleCategory(category.category_id)}>
              <div className="flex items-center gap-3">
                {expandedCategories[category.category_id] ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                <span className="text-xl">{getCategoryEmoji(category.category_name)}</span>
                <div><h4 className="font-semibold" style={{ color: THEME.primary }}>{category.category_name}</h4><p className="text-xs text-gray-500">{category.parameters.length} parameters</p></div>
              </div>
            </div>
            {expandedCategories[category.category_id] && (
              <div className="border-t bg-gray-50">
                {category.parameters.map((param, index) => (
                  <div key={param.parameter_id} className={`p-4 flex items-center justify-between ${index !== category.parameters.length - 1 ? 'border-b border-gray-200' : ''} hover:bg-white`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{param.name}</span>
                        {param.is_gating && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs rounded font-medium">Gating</span>}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500"><span>ID: {param.parameter_id}</span><span>Options: {param.options?.length || 0}</span></div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); handleEdit('parameter', param); }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete('parameter', param.parameter_id); }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Tab 3: Severity Matrix - Grade & Repair Mapping
  const renderSeverityMatrix = () => (
    <div className="space-y-4">
      {/* Header explanation */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="font-semibold mb-2" style={{ color: THEME.primary }}>⚡ Severity to Grade & Repair Mapping</h3>
        <p className="text-sm text-gray-600">Severity is determined by parameter option selection. Each severity level maps to eligible grades and required repair type.</p>
      </div>

      {/* Severity Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {severityMatrix.map(severity => (
          <div key={severity.severity_id} className="bg-white rounded-lg shadow-md p-4 border-t-4 hover:shadow-lg" style={{ borderTopColor: severity.color }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: severity.bg_color }}>
                  <DynamicIcon name={severity.icon} className="w-4 h-4" style={{ color: severity.color }} />
                </div>
                <h4 className="font-bold" style={{ color: severity.color }}>{severity.name}</h4>
              </div>
              <button onClick={() => handleEdit('severity', severity)} className="p-1 text-gray-400 hover:text-blue-600 rounded"><Edit2 className="w-3 h-3" /></button>
            </div>
            
            <p className="text-xs text-gray-600 mb-3">{severity.description}</p>
            
            {/* Eligible Grades */}
            <div className="mb-3">
              <span className="text-xs text-gray-500 block mb-1">Eligible Grades:</span>
              <div className="flex flex-wrap gap-1">
                {(severity.eligible_grades || []).map(grade => (
                  <span key={grade} className="px-1.5 py-0.5 text-xs font-bold rounded" style={{ 
                    backgroundColor: grade === 'Z7' ? '#FEE2E2' : grade === 'Z6' ? '#FEF3C7' : grade === 'Z5' ? '#FFEDD5' : '#D1FAE5',
                    color: grade === 'Z7' ? '#DC2626' : grade === 'Z6' ? '#D97706' : grade === 'Z5' ? '#EA580C' : '#059669'
                  }}>{grade}</span>
                ))}
              </div>
            </div>
            
            {/* Repair Type */}
            <div className="border-t pt-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Repair:</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${severity.repair_required ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                  {severity.repair_type || 'None'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Mapping Table */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h4 className="font-semibold mb-3" style={{ color: THEME.primary }}>📊 Severity → Grade Mapping Rules</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3">Severity</th>
                <th className="text-center py-2 px-3">Z1</th>
                <th className="text-center py-2 px-3">Z2</th>
                <th className="text-center py-2 px-3">Z3</th>
                <th className="text-center py-2 px-3">Z4</th>
                <th className="text-center py-2 px-3">Z5</th>
                <th className="text-center py-2 px-3">Z6</th>
                <th className="text-center py-2 px-3">Z7</th>
                <th className="text-left py-2 px-3">Repair Type</th>
              </tr>
            </thead>
            <tbody>
              {severityMatrix.map(severity => (
                <tr key={severity.severity_id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3 font-medium" style={{ color: severity.color }}>{severity.name}</td>
                  {['Z1', 'Z2', 'Z3', 'Z4', 'Z5', 'Z6', 'Z7'].map(grade => (
                    <td key={grade} className="text-center py-2 px-3">
                      {(severity.eligible_grades || []).includes(grade) ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-4 h-4 text-gray-300 mx-auto" />
                      )}
                    </td>
                  ))}
                  <td className="py-2 px-3">
                    <span className={`text-xs px-2 py-1 rounded ${severity.repair_required ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                      {severity.repair_type || 'None'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Tab 4: Grading Engine (Z1-Z7)
  const renderGradingEngine = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="font-semibold mb-2" style={{ color: THEME.primary }}>Inward Grade System (Z1-Z7)</h3>
        <p className="text-sm text-gray-600">Grades are determined by QC parameter responses. Each grade maps to selling condition and warranty period.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {grades.map(grade => (
          <div key={grade.grade_id} className="bg-white rounded-lg shadow-md p-4 border-l-4" style={{ borderLeftColor: grade.color }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: grade.color }}>{grade.grade_id}</div>
                <div><h4 className="font-bold text-gray-800">{grade.name}</h4><p className="text-xs text-gray-500">{grade.description?.substring(0, 30)}...</p></div>
              </div>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-gray-500">Max Deduction:</span><span className="font-medium">{grade.max_deduction_percent}%</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Selling Grade:</span><span className="font-medium px-2 py-0.5 rounded" style={{ backgroundColor: grade.bg_color, color: grade.color }}>{grade.selling_grade}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Warranty:</span><span className="font-medium">{grade.warranty_months} months</span></div>
              {grade.fixed_price && <div className="flex justify-between"><span className="text-gray-500">Fixed Price:</span><span className="font-medium text-red-600">₹{grade.fixed_price}</span></div>}
            </div>
            <button onClick={() => handleEdit('grade', grade)} className="mt-3 w-full text-xs py-1.5 border rounded hover:bg-gray-50" style={{ borderColor: THEME.primary, color: THEME.primary }}>Edit Grade</button>
          </div>
        ))}
      </div>
    </div>
  );

  // Tab 5: Pricing Engine
  const renderPricingEngine = () => (
    <div className="space-y-6">
      {/* Formula Overview */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="font-semibold mb-2" style={{ color: THEME.primary }}>💰 Pricing Formula</h3>
        <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
          <span className="text-green-600 font-bold">Final Price</span> = <span className="text-blue-600">MBP</span> - <span className="text-red-600">Σ(MBP × Weight% × Multiplier)</span> + <span className="text-purple-600">Top-ups</span>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
          <div className="p-2 bg-blue-50 rounded"><span className="font-medium text-blue-700">MBP</span><p className="text-blue-600">Maximum Buyback Price (from Cashify)</p></div>
          <div className="p-2 bg-red-50 rounded"><span className="font-medium text-red-700">Deduction</span><p className="text-red-600">Weight × Multiplier per issue</p></div>
          <div className="p-2 bg-purple-50 rounded"><span className="font-medium text-purple-700">Top-ups</span><p className="text-purple-600">Packaging accessories value</p></div>
          <div className="p-2 bg-green-50 rounded"><span className="font-medium text-green-700">Salvage</span><p className="text-green-600">Fixed ₹{formula?.salvage_price || 500} for Z7</p></div>
        </div>
      </div>

      {/* Category Weights */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold" style={{ color: THEME.primary }}>📊 Category Weights</h3>
          <span className="text-xs text-gray-500">Weight% of MBP affected by issues in each category</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b"><th className="text-left py-2 px-3">Category</th><th className="text-center py-2 px-3">OLED Weight</th><th className="text-center py-2 px-3">LCD Weight</th><th className="text-center py-2 px-3">Actions</th></tr></thead>
            <tbody>
              {weights.map(w => (
                <tr key={w.category_id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3 flex items-center gap-2"><DynamicIcon name={w.icon} className="w-4 h-4 text-gray-500" />{w.name}</td>
                  <td className="text-center py-2 px-3">
                    <input type="number" className="w-16 px-2 py-1 border rounded text-center" value={w.weight_oled} onChange={(e) => handleInlineUpdate('weight', w.category_id, 'weight_oled', parseInt(e.target.value))} />%
                  </td>
                  <td className="text-center py-2 px-3">
                    <input type="number" className="w-16 px-2 py-1 border rounded text-center" value={w.weight_lcd} onChange={(e) => handleInlineUpdate('weight', w.category_id, 'weight_lcd', parseInt(e.target.value))} />%
                  </td>
                  <td className="text-center py-2 px-3"><button className="text-blue-600 hover:text-blue-800 text-xs">Save</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Multipliers */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="font-semibold mb-4" style={{ color: THEME.primary }}>⚡ Severity Multipliers</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {multipliers.map(m => (
            <div key={m.multiplier_id} className="p-3 rounded-lg border-2 text-center" style={{ borderColor: m.color, backgroundColor: m.color + '10' }}>
              <div className="text-2xl font-bold" style={{ color: m.color }}>{m.value}x</div>
              <div className="text-sm font-medium">{m.name}</div>
              <div className="text-xs text-gray-500">{m.severity}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Top-ups */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold" style={{ color: THEME.primary }}>🎁 Packaging Top-ups</h3>
          <button onClick={() => handleAdd('topup')} className="px-3 py-1.5 text-sm rounded-lg text-white" style={{ backgroundColor: THEME.primary }}><Plus className="w-4 h-4 inline mr-1" />Add Top-up</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {topups.filter(t => t.is_active).map(t => (
            <div key={t.topup_id} className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
              <DynamicIcon name={t.icon} className="w-6 h-6 mx-auto text-green-600 mb-1" />
              <div className="text-sm font-medium text-gray-800">{t.name}</div>
              <div className="text-lg font-bold text-green-600">+₹{t.amount}</div>
              <button onClick={() => handleEdit('topup', t)} className="mt-2 text-xs text-blue-600 hover:underline">Edit</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Tab 6: Parts Mapping
  const renderPartsMapping = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-semibold" style={{ color: THEME.primary }}>🔧 Parts & BOM Mapping</h3>
            <p className="text-sm text-gray-600">Map QC failure options to spare parts for auto-PNA generation</p>
          </div>
          <div className="flex gap-4 text-sm">
            <span className="text-gray-500">Parts: <strong className="text-blue-600">{partsList.length}</strong></span>
            <span className="text-gray-500">Mappings: <strong className="text-orange-500">{partsMapping.reduce((acc, p) => acc + p.total_options, 0)}</strong></span>
          </div>
        </div>
      </div>
      
      <div className="space-y-3">
        {partsMapping.map(part => (
          <div key={part.part_name} className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 border-l-4 border-orange-500" onClick={() => togglePart(part.part_name)}>
              <div className="flex items-center gap-3">
                {expandedParts[part.part_name] ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                <Wrench className="w-5 h-5 text-orange-500" />
                <div><h4 className="font-semibold text-gray-800">{part.part_name}</h4><p className="text-xs text-gray-500">{part.total_options} QC options mapped</p></div>
              </div>
            </div>
            {expandedParts[part.part_name] && (
              <div className="border-t bg-gray-50 p-4">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-gray-500"><th className="pb-2">Parameter</th><th className="pb-2">Option</th><th className="pb-2">Severity</th><th className="pb-2">Weight</th><th className="pb-2">Multiplier</th></tr></thead>
                  <tbody>
                    {part.mappings.map((m, i) => (
                      <tr key={i} className="border-t">
                        <td className="py-2 text-gray-700">{m.parameter_name}</td>
                        <td className="py-2 text-gray-600">{m.option_text}</td>
                        <td className="py-2"><span className={`px-2 py-0.5 rounded text-xs ${m.severity === 'Salvage' ? 'bg-gray-200 text-gray-700' : m.severity === 'High' ? 'bg-red-100 text-red-700' : m.severity === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{m.severity}</span></td>
                        <td className="py-2">{m.weight}%</td>
                        <td className="py-2">{m.multiplier}x</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Modal
  const renderModal = () => {
    if (!showAddModal) return null;
    const titles = { 'type': 'QC Type', 'severity': 'Severity Level', 'category': 'Category', 'parameter': 'Parameter', 'topup': 'Top-up', 'part': 'Part', 'grade': 'Grade' };
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
          <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-bold" style={{ color: THEME.primary }}>{editingItem ? 'Edit' : 'Add'} {titles[modalType]}</h3>
            <button onClick={() => setShowAddModal(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {modalType === 'topup' && (
              <>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Top-up ID *</label><input type="text" value={formData.topup_id || ''} onChange={(e) => setFormData({...formData, topup_id: e.target.value.toLowerCase().replace(/\s+/g, '_')})} className="w-full px-3 py-2 border rounded-lg" required disabled={!!editingItem} /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label><input type="text" value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label><input type="number" value={formData.amount || 0} onChange={(e) => setFormData({...formData, amount: parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-lg" required /></div>
              </>
            )}
            {modalType === 'type' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Stage ID *</label><input type="text" value={formData.stage_id || ''} onChange={(e) => setFormData({...formData, stage_id: e.target.value.toLowerCase()})} className="w-full px-3 py-2 border rounded-lg" required disabled={!!editingItem} /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Color</label><input type="color" value={formData.color || THEME.primary} onChange={(e) => setFormData({...formData, color: e.target.value})} className="w-full h-10 border rounded-lg" /></div>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label><input type="text" value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label><input type="text" value={formData.full_name || ''} onChange={(e) => setFormData({...formData, full_name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Purpose *</label><textarea value={formData.purpose || ''} onChange={(e) => setFormData({...formData, purpose: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows={2} required /></div>
              </>
            )}
            {modalType === 'severity' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Severity ID *</label><input type="text" value={formData.severity_id || ''} onChange={(e) => setFormData({...formData, severity_id: e.target.value.toLowerCase()})} className="w-full px-3 py-2 border rounded-lg" required disabled={!!editingItem} /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Name *</label><input type="text" value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required /></div>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows={2} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Color</label><input type="color" value={formData.color || '#6B7280'} onChange={(e) => setFormData({...formData, color: e.target.value})} className="w-full h-10 border rounded-lg" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Background</label><input type="color" value={formData.bg_color || '#F3F4F6'} onChange={(e) => setFormData({...formData, bg_color: e.target.value})} className="w-full h-10 border rounded-lg" /></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Eligible Grades</label>
                  <div className="flex flex-wrap gap-2">
                    {['Z1', 'Z2', 'Z3', 'Z4', 'Z5', 'Z6', 'Z7'].map(grade => (
                      <label key={grade} className="flex items-center gap-1 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={(formData.eligible_grades || []).includes(grade)}
                          onChange={(e) => {
                            const grades = formData.eligible_grades || [];
                            if (e.target.checked) {
                              setFormData({...formData, eligible_grades: [...grades, grade].sort()});
                            } else {
                              setFormData({...formData, eligible_grades: grades.filter(g => g !== grade)});
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600"
                        />
                        <span className="text-sm font-medium">{grade}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={formData.repair_required || false}
                        onChange={(e) => setFormData({...formData, repair_required: e.target.checked})}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-700">Repair Required</span>
                    </label>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Repair Type</label><input type="text" value={formData.repair_type || ''} onChange={(e) => setFormData({...formData, repair_type: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g., Minor Repair" /></div>
                </div>
              </>
            )}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={submitting} className="flex items-center gap-2 px-6 py-2 text-white rounded-lg" style={{ backgroundColor: THEME.primary }}>
                {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{editingItem ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Main tabs config
  const tabs = [
    { id: 'types', label: 'QC Types', icon: Layers, count: qcTypes.length },
    { id: 'parameters', label: 'Parameters', icon: Settings, count: stats.parameters || 0 },
    { id: 'severity', label: 'Severity', icon: AlertTriangle, count: severityMatrix.length },
    { id: 'grading', label: 'Grading (Z1-Z7)', icon: Award, count: grades.length },
    { id: 'pricing', label: 'Pricing Engine', icon: DollarSign, count: null },
    { id: 'parts', label: 'Parts Mapping', icon: Wrench, count: partsList.length }
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6" data-testid="qc-control-module">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: THEME.primary }}>🎯 QC Control Hub</h1>
            <p className="text-gray-600 mt-2">Master configuration for the 5-stage QC lifecycle</p>
          </div>
          <div className="flex gap-3">
            <button onClick={fetchData} disabled={loading} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />Refresh
            </button>
            {['types', 'severity', 'parameters'].includes(activeTab) && (
              <button onClick={() => handleAdd(activeTab === 'types' ? 'type' : activeTab === 'severity' ? 'severity' : 'parameter')} className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2" style={{ backgroundColor: THEME.primary }}>
                <Plus className="w-5 h-5" />Add {activeTab === 'types' ? 'QC Type' : activeTab === 'severity' ? 'Severity' : 'Parameter'}
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3"><div className="p-2 rounded-full" style={{ backgroundColor: THEME.primaryLight }}><Layers className="w-5 h-5" style={{ color: THEME.primary }} /></div><div><p className="text-xl font-bold" style={{ color: THEME.primary }}>{stats.qc_types || 0}</p><p className="text-xs text-gray-500">QC Stages</p></div></div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3"><div className="p-2 rounded-full bg-green-100"><Settings className="w-5 h-5 text-green-600" /></div><div><p className="text-xl font-bold text-green-600">{stats.parameters || 0}</p><p className="text-xs text-gray-500">Parameters</p></div></div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3"><div className="p-2 rounded-full bg-orange-100"><AlertTriangle className="w-5 h-5 text-orange-500" /></div><div><p className="text-xl font-bold text-orange-500">{severityMatrix.length}</p><p className="text-xs text-gray-500">Severity Levels</p></div></div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3"><div className="p-2 rounded-full bg-blue-100"><Award className="w-5 h-5 text-blue-600" /></div><div><p className="text-xl font-bold text-blue-600">{grades.length}</p><p className="text-xs text-gray-500">Grades</p></div></div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3"><div className="p-2 rounded-full bg-purple-100"><DollarSign className="w-5 h-5 text-purple-600" /></div><div><p className="text-xl font-bold text-purple-600">{weights.length}</p><p className="text-xs text-gray-500">Weight Rules</p></div></div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3"><div className="p-2 rounded-full bg-red-100"><Wrench className="w-5 h-5 text-red-500" /></div><div><p className="text-xl font-bold text-red-500">{partsList.length}</p><p className="text-xs text-gray-500">Parts</p></div></div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b overflow-x-auto">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'border-b-2' : 'text-gray-500'}`} style={activeTab === tab.id ? { borderColor: THEME.primary, color: THEME.primary } : {}} data-testid={`tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" />{tab.label}{tab.count !== null && <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{tab.count}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Search (Parameters tab) */}
        {activeTab === 'parameters' && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="relative"><Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" /><input type="text" placeholder="Search parameters..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg" /></div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" /><p className="mt-3 text-gray-500">Loading QC configuration...</p></div>
        ) : (
          <>
            {activeTab === 'types' && renderQCTypes()}
            {activeTab === 'parameters' && renderParameters()}
            {activeTab === 'severity' && renderSeverityMatrix()}
            {activeTab === 'grading' && renderGradingEngine()}
            {activeTab === 'pricing' && renderPricingEngine()}
            {activeTab === 'parts' && renderPartsMapping()}
          </>
        )}

        {renderModal()}
      </div>
    </Layout>
  );
}
